import React, { useCallback, useContext, useEffect, useState } from 'react';
import useQueryParams from 'hooks/useQueryParams';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { useTranslation } from 'react-i18next';
import api from 'api';
import { Navigate } from 'react-router-dom';
import { Button } from 'ncoded-component-library';
import AuthContext from 'providers/Auth/Auth.context';
import { SUCCESS_MESSAGES } from 'request-responses';
import { PROFILE_ROUTE, SIGNUP_ROUTE } from 'data/global';
import FourDotLoader from 'components/FourDotLoader';
import Illustration from 'assets/images/man-with-laptop.webp';
import ResolveImage from 'components/ResolveImage';
import credentialsService from 'services/credentialsService';

import './EmailVerification.styles.scss';

type EmailVerificationProps = {
  className?: string;
};

const EmailVerification: React.FC<EmailVerificationProps> = () => {
  const [isVerifying, setIsVerifying] = useState(true);

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);
  const { onLoginReq } = useContext(AuthContext);

  const {
    params: { token, leadId, fullSlugNameForEnquire },
  } = useQueryParams();

  const { t } = useTranslation();

  const verificationRequest = useCallback(
    async (token) => {
      try {
        setIsVerifying(true);

        const {
          data: { ndaUrl, ...rest },
        } = await api.auth.verifyEmail(token, leadId);

        popSuccess(t(SUCCESS_MESSAGES.VERIFIED_EMAIL));

        if (ndaUrl) {
          onLoginReq(rest, ndaUrl, true);
        } else {
          onLoginReq(rest, fullSlugNameForEnquire || PROFILE_ROUTE);
        }
      } catch (e) {
        popServerError(e);
      } finally {
        setIsVerifying(false);
      }
    },
    [fullSlugNameForEnquire, leadId, onLoginReq, popServerError, popSuccess, t],
  );

  useEffect(() => {
    if (token) {
      // used for phone number verification reminder
      credentialsService.hasJustRegistered = 'true';
      verificationRequest(token);
    }
  }, [token, verificationRequest]);

  if (!token) return <Navigate to={SIGNUP_ROUTE} replace />;

  if (isVerifying) return <FourDotLoader />;

  return (
    <article className="email-verification">
      <p>{t('emailVerificationError-message')}</p>

      <ResolveImage asIllustration alt="Man with laptop." src={Illustration} />

      <Button
        type="submit"
        disabled={isVerifying}
        data-loading={isVerifying}
        onClick={() => verificationRequest(token)}
      >
        {t('tryAgain')}
      </Button>
    </article>
  );
};

export default EmailVerification;
